import * as angular from "angular";
import "angular-ui-bootstrap";
import {
	dateInputValue,
	dateStoredValue,
	FormRuntime,
	JsonSchemaDefinition
} from "../runtime";
import type { ControlState } from "./control-state";
import {
	formTimeZone,
	TimeZoneAppState
} from "./time-zone-context";
import template from "./datetime-control.html";
import wallDateInput from "./wall-date-input";
import confirmClear from "../../../behaviors/confirm-clear";

interface UserProfile {
	getUserProfile(): {
		timeDisplay?: string;
	};
}

export interface DatetimeControlController {
	control: ControlState;
}

// AngularJS assigns bindings before constructing the controller.
// eslint-disable-next-line no-redeclare
export class DatetimeControlController implements angular.IController {
	public static $inject = ["appState", "$userProfile"];

	public input: Date | string | null;

	public timepickerInput: Date | null;

	public calendarInput: Date | null;

	public isDateOnly = false;

	public isTwelveHour = false;

	public datepickerOpen = false;

	public timepickerOpen = false;

	public inputFormat = "MM/DD/YY HH:mm";

	public readonly dateOptions = {
		formatYear: "yyyy",
		startingDay: 0
	};

	private inputValue: unknown;

	private inputRuntime?: FormRuntime;

	private inputSchema?: JsonSchemaDefinition;

	private inputWall?: string;

	public constructor(
		private appState: TimeZoneAppState,
		private $userProfile: UserProfile
	) {}

	public $onChanges(): void {
		this.isDateOnly = this.control.schema.format === "date" || this.control.schema.metadata?.dateOnly === true;
		this.isTwelveHour = this.$userProfile.getUserProfile().timeDisplay === "12h";
		this.inputFormat = this.isDateOnly ? "MM/DD/YY" : this.isTwelveHour ? "MM/DD/YY hh:mm A" : "MM/DD/YY HH:mm";
		this.refreshInput();
	}

	public change(): void {
		if(typeof this.input === "string") {
			this.control.change(this.input || undefined);
			return;
		}

		if(!(this.input instanceof Date) || Number.isNaN(this.input.getTime())) {
			this.control.change(undefined);
			return;
		}

		const date = [this.input.getUTCFullYear(), this.pad(this.input.getUTCMonth() + 1), this.pad(this.input.getUTCDate())].join("-");
		const input = this.isDateOnly ? date : `${date}T${this.pad(this.input.getUTCHours())}:${this.pad(this.input.getUTCMinutes())}`;
		const value = dateStoredValue(input, this.control.schema, this.timeZone());

		this.control.change(value);
	}

	public changeTime(): void {
		if(!(this.timepickerInput instanceof Date) || Number.isNaN(this.timepickerInput.getTime())) {
			return;
		}

		if(!(this.input instanceof Date)) {
			const today = new Date();
			this.input = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
		}

		this.input = new Date(this.input.getTime());
		this.input.setUTCHours(this.timepickerInput.getHours(), this.timepickerInput.getMinutes(), 0, 0);
		this.change();
	}

	public changeDate(): void {
		if(!(this.calendarInput instanceof Date) || Number.isNaN(this.calendarInput.getTime())) {
			return;
		}

		this.input = new Date(this.input instanceof Date ? this.input.getTime() : 0);
		this.input.setUTCFullYear(this.calendarInput.getFullYear(), this.calendarInput.getMonth(), this.calendarInput.getDate());
		this.datepickerOpen = false;
		this.change();
	}

	public openTimepicker(): void {
		if(this.control.canEdit()) {
			this.timepickerOpen = !this.timepickerOpen;
		}
	}

	public selectToday(): void {
		if(this.control.canEdit()) {
			this.calendarInput = new Date();
			this.changeDate();
		}
	}

	public clear(): void {
		if(!this.control.canEdit()) {
			return;
		}

		this.input = null;
		this.calendarInput = null;
		this.timepickerInput = null;
		this.datepickerOpen = false;
		this.timepickerOpen = false;

		this.control.change(undefined);
	}

	private refreshInput(): void {
		const input = dateInputValue(this.control.value, this.control.schema, this.timeZone());

		if(this.inputValue === this.control.value && this.inputRuntime === this.control.session.runtime
			&& this.inputSchema === this.control.schema && this.inputWall === input) {
			return;
		}

		this.inputValue = this.control.value;
		this.inputRuntime = this.control.session.runtime;
		this.inputSchema = this.control.schema;
		this.inputWall = input;
		// This carrier represents wall components only, so device DST cannot shift a target-zone time.
		const carrier = input ? new Date(`${input}${this.isDateOnly ? "T00:00:00" : ":00"}Z`) : undefined;
		this.input = carrier ?? (typeof this.control.value === "string" ? this.control.value : null);
		// UI Bootstrap's timepicker uses local getters and ignores ngModelOptions.timezone.
		this.timepickerInput = carrier ? new Date(2000, 0, 15, carrier.getUTCHours(), carrier.getUTCMinutes()) : null;
		this.calendarInput = carrier ? new Date(carrier.getUTCFullYear(), carrier.getUTCMonth(), carrier.getUTCDate(), 12) : null;
	}

	private pad(value: number): string {
		return String(value).padStart(2, "0");
	}

	private timeZone(): string | undefined {
		const entityTimeZone = this.control.session.timeZones.get(this.control.contextPath);

		return formTimeZone(this.control.schema, this.appState.forms?.selected, entityTimeZone);
	}
}

export default angular.module("vesl.extensions.json-schema.datetime-control", ["ui.bootstrap", wallDateInput, confirmClear])
	.component("jsonSchemaDatetimeControl", {
		template,
		controller: DatetimeControlController,
		bindings: { control: "<" }
	})
	.name;
